import { Skeleton } from "@/components/ui/alert"
import { FormField } from "@/components/ui/form-field"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import {
  AUTO,
  DETECTOR_SETTING_KEYS,
  valueLabel,
  type DetectorProfile,
  type DetectorProfileOptions,
  type DetectorSettingKey,
} from "./profile"

/**
 * What Automatic falls back to where the fields are shown.
 *
 * At intake it is the engagement's profile first and the deployment default
 * after; on an engagement's own profile it is the deployment default alone.
 */
export type AutoMeans = "engagement-then-default" | "default"

export interface DetectorProfileFieldsProps {
  /** Undefined while the options load: each field is a skeleton, never a guessed list. */
  options: DetectorProfileOptions | undefined
  value: DetectorProfile
  onChange: (next: DetectorProfile) => void
  autoMeans: AutoMeans
  disabled?: boolean
}

/**
 * The seven detector settings as selects, each starting at Automatic.
 *
 * Every label and help line is the server's, from the options route. The help
 * under a field is the chosen value's, so the reviewer reads what their choice
 * does rather than a description of the setting in general.
 */
export function DetectorProfileFields({
  options,
  value,
  onChange,
  autoMeans,
  disabled = false,
}: DetectorProfileFieldsProps): JSX.Element {
  if (!options) {
    return (
      <div className="grid gap-3 sm:grid-cols-2" aria-busy="true">
        {DETECTOR_SETTING_KEYS.map((key) => (
          <div key={key} className="space-y-1.5">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
      </div>
    )
  }

  const set = (key: DetectorSettingKey, next: string) => {
    onChange({ ...value, [key]: next })
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {options.settings.map((setting) => {
        const chosen = value[setting.key] ?? AUTO
        const help = setting.values.find((v) => v.value === chosen)?.help
        const fallback = options.deploymentDefault[setting.key]
        const autoHint =
          chosen === AUTO && fallback !== undefined
            ? autoMeans === "default"
              ? `Here: ${valueLabel(options, setting.key, fallback)}.`
              : `The engagement's setting, else ${valueLabel(options, setting.key, fallback)}.`
            : null
        return (
          <FormField
            key={setting.key}
            label={setting.label}
            hint={[help, autoHint].filter(Boolean).join(" ") || undefined}
            disabled={disabled}
          >
            <Select
              value={chosen}
              onValueChange={(next) => {
                set(setting.key, next)
              }}
              disabled={disabled}
            >
              <SelectTrigger selectSize="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {setting.values.map((v) => (
                  <SelectItem key={v.value} value={v.value} description={v.help || undefined}>
                    {v.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        )
      })}
    </div>
  )
}
